import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import {
  BOOKING_PROCESS_NAME,
  BOATERSNETWORK_BOOKING_PROCESS_NAME,
} from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserMergeFailuresAlert } from '../../ducks/user.duck';

const MY_BOOKINGS_PAGE_SIZE = 50;

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Slice ================ //

const myBookingsPageSlice = createSlice({
  name: 'MyBookingsPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    transactionRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(action.payload.data.data);
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default myBookingsPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = ({ params }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { tab } = params;

  const onlyFilterValues = {
    customer: 'order',
    provider: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for MyBookingsPage: ${tab}`));
  }

  const apiQueryParams = {
    only: onlyFilter,
    processNames: [BOOKING_PROCESS_NAME, BOATERSNETWORK_BOOKING_PROCESS_NAME],
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType', 'publicData.categoryLevel1'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    perPage: MY_BOOKINGS_PAGE_SIZE,
    sort: 'createdAt',
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('MyBookingsPage/loadData', loadDataPayloadCreator);

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  // Push 8 Phase A.4e-2b — refresh merge-failure alert state on every
  // My Bookings visit so the page-level banner + per-card visual
  // reflect the current state (currentUser fetch only fires on login;
  // mid-session failure recordings wouldn't be visible otherwise).
  // Fire-and-forget; never blocks the page render.
  dispatch(fetchCurrentUserMergeFailuresAlert()).catch(() => {});
  return dispatch(loadDataThunk({ params, search }));
};
